<template>
  <div
    style="height: 100%"
    class="d-flex justify-center align-center"
    :class="canChangePhoto || canDeletePhoto ? 'relative' : ''"
  >
    <v-skeleton-loader
      v-if="isLoading"
      type="image"
      style="border-radius: 50%"
      :width="size"
      :height="size"
    />
    <v-avatar v-else :size="`${size} !important`">
      <ImageMedia
        v-if="userHasPhoto && !hasError"
        class="avatar-content"
        data-private
        data-testid="image-media"
        :size="imageSize"
        :src="accountPhoto"
        :class="{ blackandwhite: account.isDeleted }"
        :width="size"
        :height="size"
        @error="hasError = true"
      >
        <v-overlay v-if="account.isDeleted" absolute color="white" />
      </ImageMedia>
      <div
        v-else
        class="d-flex justify-center align-center avatar-content"
        :class="
          account.isDeleted
            ? `${backgroundColor} blackandwhite`
            : backgroundColor
        "
      >
        <span
          class="name-letters font-weight-medium white--text"
          :style="{ 'font-size': fontSize }"
          >{{ accountLetters }}</span
        >
        <v-overlay v-if="account.isDeleted" absolute color="white" />
      </div>

      <input
        v-if="canChangePhoto"
        id="fileInput"
        class="fileInput hidden d-none"
        type="file"
        name="image"
        data-private
        @change="onFileChange"
      />
    </v-avatar>
    <v-btn
      v-if="canChangePhoto && !isLoading"
      class="primary absolute add-picture"
      icon
      small
      @click="handleAddPhotoClick"
    >
      <v-icon color="white" size="16">mdi-camera-plus</v-icon>
    </v-btn>
    <Button
      v-if="userHasPhoto && canDeletePhoto && !isLoading"
      icon="mdi-close"
      color="blue-grey"
      class="blue-grey absolute delete-picture"
      size="small"
      @click="$emit('photoUploaded', null)"
    />
  </div>
</template>

<script>
import mixin from '@/mixins/mixin.vue'

import Button from '@/components/UI/Button'
import ImageMedia from '@/components/ImageMedia/ImageMedia.vue'

import { IMAGE_SIZES } from 'rever-media-sdk'
import ReverMediaSDK from '@/lib/ReverMediaSDK'
import getEntityId from '@/lib/getEntityId'
import _ from 'lodash'

const AVATAR_COLORS = [
  'blue darken-1',
  'blue',
  'blue lighten-1',
  'purple darken-1',
  'purple',
  'purple lighten-1',
  'red darken-1',
  'red',
  'red lighten-1',
  'orange darken-1',
  'orange',
  'orange lighten-1',
  'yellow darken-1',
  'yellow',
  'yellow lighten-1',
  'green darken-1',
  'green',
  'green lighten-1',
  'teal darken-1',
  'teal',
  'teal lighten-1',
  'blue-grey darken-1',
  'blue-grey',
  'blue-grey lighten-1',
]

export default {
  name: 'UserAvatar',

  components: {
    ImageMedia,
    Button,
  },

  mixins: [mixin],

  props: {
    imageSize: {
      type: String,
      required: false,
      default: IMAGE_SIZES.SQUARE,
    },
    size: {
      type: String,
      required: false,
      default: '108px',
    },
    fontSize: {
      type: String,
      required: false,
      default: '35px',
    },
    account: {
      type: Object,
      required: true,
    },
    isLoading: {
      type: Boolean,
      required: false,
      default: false,
    },
    canChangePhoto: {
      type: Boolean,
      required: false,
      default: false,
    },
    canDeletePhoto: {
      type: Boolean,
      required: false,
      default: false,
    },
  },

  data() {
    return {
      hasError: false,
      IMAGE_SIZES,
    }
  },

  computed: {
    userHasPhoto() {
      if (_.isEmpty(this.account?.photo)) return false

      return Boolean(getEntityId(this.account?.photo))
    },

    accountId() {
      return (
        getEntityId(this.account) ?? this.account?.userId ?? this.account?.email
      )
    },

    accountPhoto() {
      return this.account?.photo ?? null
    },

    accountLetters() {
      const displayName = this.account.displayname?.trim?.() || ''
      const familyName = this.account.familyname?.trim?.() || ''

      if (displayName && familyName) {
        return `${displayName.charAt(0)}${familyName.charAt(0)}`.toUpperCase()
      }

      if (displayName && !familyName) {
        return displayName.substring(0, 2).toUpperCase()
      }

      return this.account?.email?.substring?.(0, 2)?.toUpperCase?.()
    },

    backgroundColor() {
      let sum = 0
      for (const index in this.accountId) {
        sum += this.accountId.charCodeAt(index)
      }
      const index = sum % AVATAR_COLORS.length

      return AVATAR_COLORS[index]
    },
  },

  methods: {
    handleAddPhotoClick() {
      document.getElementById('fileInput').click()
    },

    onFileChange(e) {
      const files = e.target.files || e.dataTransfer.files
      if (!files.length) return

      this.uploadImage(files[0])
    },

    async uploadImage(file) {
      try {
        const response = await ReverMediaSDK.uploadMedia(file)

        this.$emit('photoUploaded', response)
      } catch (err) {
        this.showError(`${this.$t('error.image_upload')} ${err.message}`)
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.avatar-content {
  width: 100%;
  height: 100%;
}

.add-picture {
  bottom: 5px;
  right: 5px;
}

.blackandwhite {
  filter: gray;
  filter: grayscale(100%);
  opacity: 0.7;
}
.relative {
  &:hover .delete-picture {
    visibility: visible;
  }

  .delete-picture {
    top: 5px;
    right: 5px;
    visibility: hidden;
  }
}
</style>
